import React, { useCallback, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import DeviceSearch from "../controls/DeviceSearch";
import DeviceFilters from "../controls/DeviceFilters";
import DeviceList from "../controls/DeviceList";
import CustomDeviceForm from "../controls/CustomDeviceForm";
import Button from "../common/Button";
import ConfirmDialog from "../common/ConfirmDialog";
import { filterDevices, getBrands, getYearFilterOptions } from "../../utils/deviceUtils";

const INITIAL_FILTERS = { category: "all", brand: "all", year: "all", size: "all", orientation: "all" };

function Sidebar({ deviceSelection }) {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const {
    allDevices,
    devicesById,
    selectedDeviceIds,
    favoriteIds,
    recentIds,
    toggleDevice,
    toggleFavorite,
    deleteCustomDevice,
    selectAll,
    clearSelection,
    addCustomDevice,
  } = deviceSelection;

  const brands = useMemo(() => getBrands(allDevices), [allDevices]);
  const yearOptions = useMemo(() => getYearFilterOptions(allDevices), [allDevices]);
  const filtered = useMemo(
    () => filterDevices(allDevices, { search, ...filters }),
    [allDevices, search, filters]
  );
  const isFiltered = search.trim().length > 0 || Object.values(filters).some((v) => v !== "all");

  const resetFilters = useCallback(() => {
    setSearch("");
    setFilters(INITIAL_FILTERS);
    // DeviceSearch owns its own (debounced) input text locally; remounting
    // it is the simplest way to actually clear what's visible on screen
    // rather than just resetting the filter state that reads from it.
    setSearchResetKey((prev) => prev + 1);
  }, []);

  const favoriteDevices = useMemo(
    () => favoriteIds.map((id) => devicesById.get(id)).filter(Boolean),
    [favoriteIds, devicesById]
  );
  const pendingDeleteDevice = pendingDeleteId ? devicesById.get(pendingDeleteId) : null;
  const cancelDelete = useCallback(() => setPendingDeleteId(null), []);
  const confirmDelete = useCallback(() => {
    deleteCustomDevice(pendingDeleteId);
    setPendingDeleteId(null);
  }, [deleteCustomDevice, pendingDeleteId]);

  const recentDevices = useMemo(
    () => recentIds.map((id) => devicesById.get(id)).filter(Boolean),
    [recentIds, devicesById]
  );

  return (
    <div className="sidebar-content">
      <div className="sidebar-controls">
        <DeviceSearch key={searchResetKey} onSearch={setSearch} />
        <DeviceFilters
          category={filters.category}
          setCategory={(category) => setFilters((prev) => ({ ...prev, category }))}
          brand={filters.brand}
          setBrand={(brand) => setFilters((prev) => ({ ...prev, brand }))}
          brands={brands}
          year={filters.year}
          setYear={(year) => setFilters((prev) => ({ ...prev, year }))}
          years={yearOptions.years}
          hasOlderYears={yearOptions.hasOlder}
          size={filters.size}
          setSize={(size) => setFilters((prev) => ({ ...prev, size }))}
          orientation={filters.orientation}
          setOrientation={(orientation) => setFilters((prev) => ({ ...prev, orientation }))}
        />
      </div>

      <div className="sidebar-scroll">
        <DeviceList
          devices={filtered}
          favoriteDevices={favoriteDevices}
          recentDevices={recentDevices}
          selectedDeviceIds={selectedDeviceIds}
          favoriteIds={favoriteIds}
          onToggle={toggleDevice}
          onToggleFavorite={toggleFavorite}
          onDelete={setPendingDeleteId}
          onSelectAll={selectAll}
          onClearSelection={clearSelection}
          onResetFilters={resetFilters}
          isFiltered={isFiltered}
        />

        <Button variant="secondary" className="btn-block create-device-btn" onClick={() => setShowCustomForm(true)}>
          <Plus size={16} strokeWidth={1.75} aria-hidden="true" />
          Create custom device
        </Button>
      </div>

      {showCustomForm && (
        <CustomDeviceForm
          existingDevices={allDevices}
          onCreate={addCustomDevice}
          onClose={() => setShowCustomForm(false)}
        />
      )}

      {pendingDeleteDevice && (
        <ConfirmDialog
          title="Delete custom device"
          message={`Delete "${pendingDeleteDevice.name}"? It will also be removed from your selection and favorites.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </div>
  );
}

export default Sidebar;
